import { z } from 'zod';
import { PageMetaSchema, PageQuerySchema, SortDirectionSchema } from './pagination.js';
import { TENANT_MODULE_KEYS } from './tenant-modules.js';
/**
 * Pending hospital signups, as Control sees them.
 *
 * WHY THIS IS A SEPARATE CONTRACT FROM `organisation`. A signup is a REQUEST,
 * not a customer. It may be rejected, it may be spam, it may be a duplicate of
 * an organisation that already exists. Modelling it as a half-built
 * organisation would put rows in the customer estate that are not customers —
 * exactly the kind of "a number on a screen that isn't real" this whole
 * programme exists to remove. An organisation is created FROM a signup at the
 * moment of approval, and the two stay linked by `organisationId`.
 *
 * WHAT IT DELIBERATELY DOES NOT CARRY. No password, no staff roster, no
 * clinical anything. The tenant-side projection already refuses those, but this
 * schema is `.strict()` so that a payload which grew a field upstream is
 * REJECTED at Control's boundary rather than silently stored. Two independent
 * refusals, because a single boundary is one mistake away from a leak.
 */
export const SIGNUP_STATUSES = ['pending', 'approved', 'rejected'];
export const SignupStatusSchema = z.enum(SIGNUP_STATUSES);
export const SIGNUP_INGEST_SOURCES = ['webhook', 'backfill'];
export const SignupIngestSourceSchema = z.enum(SIGNUP_INGEST_SOURCES);
export const TenantSignupSchema = z
    .object({
    id: z.uuid(),
    /** The tenant's own SignupRequest id. Stable across redelivery. */
    tenantSignupId: z.string().min(1).max(128),
    organisationName: z.string().min(1).max(200),
    organisationType: z.string().min(1).max(64),
    deploymentPreference: z.string().min(1).max(32),
    contactName: z.string().min(1).max(200),
    contactEmail: z.email().max(320),
    declaredBedCount: z.number().int().nonnegative().nullable(),
    declaredDailyOpdVolume: z.number().int().nonnegative().nullable(),
    requestedModules: z.array(z.enum(TENANT_MODULE_KEYS)),
    staffingShape: z.string().max(32).nullable(),
    sourceIpCountry: z.string().length(2).nullable(),
    /**
     * WHEN THE HOSPITAL SIGNED UP, not when Control heard about it.
     *
     * These are two different facts and the distinction is the whole point of
     * the backfill: the three hospitals Control never knew about signed up on
     * their own dates, and a screen that showed the delivery date would claim
     * they arrived the day the pipeline was switched on. `receivedAt` is kept
     * beside it precisely so the lag is visible rather than hidden.
     */
    requestedAt: z.iso.datetime(),
    receivedAt: z.iso.datetime(),
    status: SignupStatusSchema,
    decidedAt: z.iso.datetime().nullable(),
    decidedBy: z.uuid().nullable(),
    decidedByDisplayName: z.string().max(200).nullable(),
    decisionReason: z.string().max(2000).nullable(),
    organisationId: z.uuid().nullable(),
    ingestSource: SignupIngestSourceSchema,
})
    .strict();
export const TenantSignupListQuerySchema = PageQuerySchema.extend({
    status: SignupStatusSchema.optional(),
    search: z.string().max(200).optional(),
    /**
     * Defaults to newest-requested first. The operator's question is almost
     * always "what came in that I have not dealt with", so the most recent
     * pending signup must be the first thing on the screen without them sorting.
     */
    sort: z.enum(['requestedAt', 'receivedAt', 'organisationName']).default('requestedAt'),
    direction: SortDirectionSchema.default('desc'),
});
export const TenantSignupListResponseSchema = z.object({
    items: z.array(TenantSignupSchema),
    meta: PageMetaSchema,
    /**
     * Pending count for the whole table, NOT just this page.
     *
     * The navigation badge reads this. A badge computed from the current page
     * would say "3" while sitting on page two of nine, and a number on a screen
     * that is wrong in a boring way still teaches the operator not to trust the
     * screen.
     */
    pendingTotal: z.number().int().nonnegative(),
});
/**
 * Approving a signup CREATES A CUSTOMER. It is a high-risk action under this
 * repo's rules, so it captures a reason, and the reason is stored on the row
 * and in the audit trail rather than being a throwaway confirmation prompt.
 */
export const SignupApprovalSchema = z
    .object({
    reason: z.string().min(10).max(2000),
    /**
     * Optional override of the organisation name taken from the signup.
     * Hospitals type their own name into a public form; "St. Mary's" and
     * "St Marys Hospital Pvt Ltd" both arrive. The operator can correct it at
     * the moment of approval instead of creating a customer with a bad name
     * and renaming it afterwards.
     */
    organisationName: z.string().min(1).max(200).optional(),
})
    .strict();
export const SignupRejectionSchema = z
    .object({
    reason: z.string().min(10).max(2000),
})
    .strict();
/**
 * What approval produced. Returns the organisation id so the UI can navigate
 * straight to the customer that now exists, rather than making the operator
 * search for the thing they just created.
 */
export const SignupDecisionResultSchema = z.object({
    signup: TenantSignupSchema,
    organisationId: z.uuid().nullable(),
});
/**
 * The tenant's six deployment preferences onto Control's two deployment types.
 *
 * These are genuinely different vocabularies and the mismatch is not an
 * accident to be papered over: the tenant asks a prospect HOW THEY WANT TO BE
 * HOSTED (six answers, including "unsure"), while Control records WHERE THE
 * INSTALLATION RUNS (`cloud` or `on_prem` — a CHECK constraint, enforced by the
 * database). Approving a signup has to cross that boundary, so the crossing is
 * written down here, once, with tests, rather than inlined in the service where
 * the next person would have to rediscover it.
 *
 * Found by an integration test: passing `managed_cloud` straight through raised
 * `violates check constraint "organisations_deployment_type_check"` and surfaced
 * as a 500 on approve — a hospital could be approved and the operator would see
 * "internal error" with no idea why.
 *
 * WHY 'unsure' AND 'hybrid' BECOME 'cloud'. Both are real answers a prospect
 * gives, and neither is a deployment. Control's column has no third option, so
 * something must be chosen; cloud is the default the business actually sells and
 * the reversible one — an organisation can be moved to on-prem later, whereas
 * recording on-prem for a customer who never asked for it implies an
 * installation nobody agreed to. The original preference is NOT lost: it stays
 * on the signup row, so the honest answer to "what did they ask for?" survives.
 */
export const DEPLOYMENT_PREFERENCE_TO_TYPE = {
    managed_cloud: 'cloud',
    customer_managed_cloud: 'cloud',
    on_premises: 'on_prem',
    managed_on_premises: 'on_prem',
    hybrid: 'cloud',
    unsure: 'cloud',
};
export function deploymentTypeForPreference(preference) {
    /**
     * Defaults rather than throws.
     *
     * An unrecognised preference means the tenant added a hosting option Control
     * has not been taught about yet. Refusing the approval would block a real
     * customer over a vocabulary drift; defaulting to the reversible value lets
     * the operator proceed, and the signup row still carries what was actually
     * requested.
     */
    return DEPLOYMENT_PREFERENCE_TO_TYPE[preference] ?? 'cloud';
}
//# sourceMappingURL=signups.js.map